/**
 * API client for applications and the asset to application mapping
 * (apps/api-controller/src/modules/discovery/applications/handlers.ts, slice
 * D1). Reads need discovery:read; writes need discovery:applications:write
 * (DPO, Compliance Analyst). Row-level security limits a unit-scoped user to
 * their units' applications, assets and mappings and the tenant-wide ones, so
 * the page shows what the API returns.
 *
 * Slice D1c: on a tenant paired with FOCTTA Consent the list carries
 * `consent.paired`, and the page hides Add and Edit; applications come from
 * Consent through "Sync applications".
 */

import { apiFetch } from './api';

export type ApplicationSource = 'foctta_core' | 'local';
export type ApplicationStatus = 'active' | 'retired';
export type LinkScope = 'data_source' | 'asset';

export interface Owner {
  name: string | null;
  email: string | null;
}

export interface Application {
  id: string;
  source: ApplicationSource;
  /** FOCTTA Consent's application id, for an application that came from Consent. */
  coreRef: string | null;
  name: string;
  description: string | null;
  businessUnitId: string | null;
  coreBusinessUnitRef: string | null;
  applicationOwner: Owner | null;
  dataOwner: Owner | null;
  status: ApplicationStatus;
  dataSourceCount: number;
  assetOverrideCount: number;
  createdAt: string;
  updatedAt: string;
  retiredAt: string | null;
  /** A retired local application linked to this Consent application after the tenant paired. */
  linkedApplicationId: string | null;
  /** not_synced and disabled are held: only a person who sees every unit reads them. */
  unitState?: 'ok' | 'not_synced' | 'disabled';
}

/** What the list says about the pairing with FOCTTA Consent (slice D1c). */
export interface ConsentInfo {
  paired: boolean;
  /** The connector is live, so a sync can run now. */
  connectorActive: boolean;
  /** The last successful sync. */
  lastSyncedAt: string | null;
  lastSyncOutcome: 'running' | 'ok' | 'failed' | null;
  lastSyncError: string | null;
  lastSyncCounts: SyncCounts | null;
}

export interface SyncCounts {
  added: number;
  updated: number;
  retired: number;
  unchanged: number;
}

export const NOT_PAIRED: ConsentInfo = {
  paired: false,
  connectorActive: false,
  lastSyncedAt: null,
  lastSyncOutcome: null,
  lastSyncError: null,
  lastSyncCounts: null,
};

export interface DataSource {
  dataSourceKey: string;
  sourceType: string;
  assetCount: number;
  overrideCount: number;
  link: {
    id: string;
    applicationId: string;
    applicationName: string | null;
    applicationStatus: ApplicationStatus | null;
    linkSource: 'operator' | 'suggested';
    /** The unit whose assets the link decides; null means every asset of the data source. */
    businessUnitId: string | null;
    updatedAt: string;
  } | null;
}

export interface DataSourceAsset {
  assetId: string;
  displayName: string;
  sourceType: string;
  ref: string | null;
  businessUnitId: string | null;
  sensitivity: string;
  override: { linkId: string; applicationId: string; applicationName: string | null; applicationStatus?: ApplicationStatus | null } | null;
  effective: { applicationId: string; applicationName: string | null; applicationStatus?: ApplicationStatus | null; via: LinkScope } | null;
}

export interface ApplicationFields {
  name?: string;
  description?: string | null;
  businessUnitId?: string | null;
  applicationOwnerName?: string | null;
  applicationOwnerEmail?: string | null;
  dataOwnerName?: string | null;
  dataOwnerEmail?: string | null;
  status?: ApplicationStatus;
}

/** The routes' own page ceiling. */
const PAGE = 500;
/** 5 000 rows is past any real inventory; stop rather than loop on a bad total. */
const MAX_PAGES = 10;

async function allPages<T, R extends { items: T[]; total: number } = { items: T[]; total: number }>(
  path: string,
  params: Record<string, string>,
): Promise<{ items: T[]; first: R | null }> {
  const out: T[] = [];
  let first: R | null = null;
  for (let page = 0; page < MAX_PAGES; page++) {
    const qs = new URLSearchParams({ ...params, limit: String(PAGE), offset: String(page * PAGE) });
    const res = await apiFetch<R>(`${path}?${qs}`);
    first ??= res;
    out.push(...res.items);
    if (res.items.length < PAGE || out.length >= res.total) break;
  }
  return { items: out, first };
}

/** Every application, with what the first page says about the pairing with Consent. */
export async function loadApplications(status: ApplicationStatus | 'all' = 'all'): Promise<{ items: Application[]; consent: ConsentInfo }> {
  const { items, first } = await allPages<Application, { items: Application[]; total: number; consent?: ConsentInfo }>(
    '/v1/discovery/applications',
    { status },
  );
  return { items, consent: first?.consent ?? NOT_PAIRED };
}

export async function listApplications(status: ApplicationStatus | 'all' = 'all'): Promise<Application[]> {
  return (await loadApplications(status)).items;
}

export async function listDataSources(): Promise<DataSource[]> {
  return (await allPages<DataSource>('/v1/discovery/application-data-sources', {})).items;
}

export async function listDataSourceAssets(dataSourceKey: string): Promise<DataSourceAsset[]> {
  return (await allPages<DataSourceAsset>('/v1/discovery/application-data-sources/assets', { dataSourceKey })).items;
}

export async function createApplication(fields: ApplicationFields & { name: string }): Promise<Application> {
  const res = await apiFetch<{ application: Application }>('/v1/discovery/applications', {
    method: 'POST',
    body: JSON.stringify(fields),
  });
  return res.application;
}

export async function updateApplication(id: string, fields: ApplicationFields): Promise<Application> {
  const res = await apiFetch<{ application: Application }>(`/v1/discovery/applications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(fields),
  });
  return res.application;
}

export type LinkTarget = { scope: 'data_source'; dataSourceKey: string } | { scope: 'asset'; assetId: string };

export async function setLink(target: LinkTarget, applicationId: string): Promise<{ assetCount: number | null }> {
  return apiFetch<{ assetCount: number | null }>('/v1/discovery/application-links', {
    method: 'PUT',
    body: JSON.stringify({ ...target, applicationId }),
  });
}

export async function removeLink(linkId: string): Promise<void> {
  await apiFetch(`/v1/discovery/application-links/${encodeURIComponent(linkId)}`, { method: 'DELETE' });
}

/** "Sync applications" (slice D1c): pulls Consent's Application Inventory. */
export function syncApplications(): Promise<{ counts: SyncCounts; fetched: number; skipped: number }> {
  return apiFetch('/v1/discovery/applications/sync', { method: 'POST', body: JSON.stringify({}) });
}

/** Links a local application to the Consent application it is; its mappings move there and it is retired. */
export function linkToConsent(
  id: string,
  consentApplicationId: string,
): Promise<{ application: Application; target: Application; moved: { dataSources: number; assets: number } }> {
  return apiFetch(`/v1/discovery/applications/${encodeURIComponent(id)}/link-to-consent`, {
    method: 'POST',
    body: JSON.stringify({ consentApplicationId }),
  });
}
